import cn from "classnames";

import { Link } from "@/components/link/link.component";

import { appendQueryString } from "@/utils/url.util";

import {
  COMPARE_COMPETITOR_TABLE_APPEARANCES,
  COMPARE_COMPETITOR_TABLE_DEFAULTS,
  type CompareCompetitorTableAppearance,
} from "@/sanity/defaults/compareCompetitorTable.defaults";

import { columnHighlightClass } from "./compareCompetitorTable.tableUtils";
import type {
  SanityCompareCompetitorTableQueryResult,
  SanityCompetitorCompareColumn,
} from "./compareCompetitorTable.type";

export function competitorTableCtaHref(
  appearance: CompareCompetitorTableAppearance,
  ctaLink: SanityCompareCompetitorTableQueryResult["ctaLink"],
  highlightIndex: number,
): string | null {
  if (
    appearance !== COMPARE_COMPETITOR_TABLE_APPEARANCES.adt ||
    highlightIndex < 0
  ) {
    return null;
  }

  return appendQueryString(ctaLink?.url, ctaLink?.queryString);
}

export const CompareCompetitorTableCtaRow = ({
  columns,
  highlightIndex,
  appearance,
  ctaLabel,
  ctaLink,
}: CompareCompetitorTableCtaRowProps) => {
  const ctaHref = competitorTableCtaHref(appearance, ctaLink, highlightIndex);
  if (!ctaHref) return null;

  return (
    <tr data-testid="compare-competitor-table-cta-row">
      <th scope="row" className="p-0" />
      {columns.map((column, columnIndex) => (
        <td
          key={`cta-${column.id}`}
          className={cn(
            "px-2 pt-2 pb-6 text-center align-middle",
            columnHighlightClass(
              columnIndex,
              highlightIndex,
              "last",
              appearance,
            ),
          )}>
          {columnIndex === highlightIndex ? (
            <Link
              href={ctaHref}
              secondary
              className="inline-flex min-w-38 px-6"
              target={ctaLink?.openInNewTab ? "_blank" : undefined}
              rel={ctaLink?.openInNewTab ? "noopener noreferrer" : undefined}>
              {ctaLabel?.trim() || COMPARE_COMPETITOR_TABLE_DEFAULTS.ctaLabel}
            </Link>
          ) : null}
        </td>
      ))}
    </tr>
  );
};

type CompareCompetitorTableCtaRowProps = {
  columns: SanityCompetitorCompareColumn[];
  highlightIndex: number;
  appearance: CompareCompetitorTableAppearance;
  ctaLabel: SanityCompareCompetitorTableQueryResult["ctaLabel"];
  ctaLink: SanityCompareCompetitorTableQueryResult["ctaLink"];
};
